import i18nFactory from '@yandex-int/i18n';
import moment from 'moment';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { CampaignFilterStrategyType } from '../../typings/filter';
import {
    CurrencyCode,
    GdCampaignBudgetShowMode,
    GdCampaignPlatform,
    GdCampaignStrategyType
} from '../../typings/graphql-grid';
import {
    CampaignStrategyGoal,
    LogCampaignDayBudget,
    LogCampaignStrategyParams,
    LogCampaignStrategyParamValue,
    OptimizationStrategyParamNames,
    StrategyBudgetParamNames,
    StrategyParamNames
} from '../../typings/strategy';
import { DateDisplayHelper } from '../DateDisplayHelper/DateDisplayHelper';

import * as keyset from './StrategyHelpers.i18n';

// !!! ВНИМАНИЕ !!!
// ниже написанный код не рекомендуется использовать в качестве образца для подражания,
// так как он написан по большей части НЕ в соответствии с парадигмами Typescript'а

const i18n = i18nFactory(keyset);

export class StrategyHelpers {
    static MobileCPAGoalIds = [4100000000, 4];

    static getCampaignDefaultStrategyPlatformSetTitles(
        isBidCorrectionSearchEnabled?: boolean
    ): Record<GdCampaignPlatform, string> {
        return {
            [GdCampaignPlatform.SEARCH]: isBidCorrectionSearchEnabled
                ? i18n('ruchnoe-upravlenie-stavkami2')
                : i18n('ruchnoe-upravlenie-stavkami'),
            [GdCampaignPlatform.BOTH]: isBidCorrectionSearchEnabled
                ? i18n('ruchnoe-upravlenie-stavkami2')
                : i18n('ruchnoe-upravlenie-stavkami-s-optimizaciei'),
            [GdCampaignPlatform.CONTEXT]: isBidCorrectionSearchEnabled
                ? i18n('ruchnoe-upravlenie-stavkami2')
                : i18n('ruchnoe-upravlenie-stavkami-s-optimizaciei')
        };
    }

    static getCampaignStrategyNameEnumSetTitles(
        isBidCorrectionSearchEnabled?: boolean
    ): Record<GdCampaignStrategyType | CampaignFilterStrategyType, string> {
        return {
            [GdCampaignStrategyType.DEFAULT]: isBidCorrectionSearchEnabled
                ? i18n('ruchnoe-upravlenie-stavkami-generic2')
                : i18n('ruchnoe-upravlenie-stavkami-generic'),
            [GdCampaignStrategyType.NO_PREMIUM]: '', // TODO: оторвать NO_PREMIUM вместе с бэкэндом
            [GdCampaignStrategyType.AVG_CLICK]: i18n('srednyaya-cena-klika'),
            [GdCampaignStrategyType.AVG_CPA]: i18n('srednyaya-cena-konversii'),
            [GdCampaignStrategyType.AVG_CPI]: i18n(
                'srednyaya-cena-ustanovki-prilozheniya'
            ),
            [GdCampaignStrategyType.ROI]: i18n(
                'srednyaya-rentabelnost-investiciy-roi'
            ),
            [GdCampaignStrategyType.WEEK_BUDGET]: i18n('nedelnyy-byudzhet'),
            [GdCampaignStrategyType.WEEK_BUNDLE]: i18n('nedelnyy-paket-klikov'),
            [GdCampaignStrategyType.AVG_CPC_PER_CAMP]: i18n(
                'optimizaciya-kolichestva-klikov-per-camp'
            ),
            [GdCampaignStrategyType.AVG_CPC_PER_FILTER]: i18n(
                'optimizaciya-kolichestva-klikov-per-filter'
            ),
            [GdCampaignStrategyType.AVG_CPA_PER_CAMP]: i18n(
                'optimizaciya-kolichestva-konversiy-per-camp'
            ),
            [GdCampaignStrategyType.AVG_CPA_PER_FILTER]: i18n(
                'optimizaciya-kolichestva-konversiy-per-filter'
            ),
            [GdCampaignStrategyType.CPM_DEFAULT]: i18n(
                'ruchnoe-upravlenie-stavkami'
            ),
            [GdCampaignStrategyType.CPM_MAX_REACH]: i18n(
                'snizhenie-tseny-povtornyh-pokazov'
            ),
            [GdCampaignStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD]: i18n(
                'snizhenie-tseny-povtornyh-pokazov'
            ),
            [GdCampaignStrategyType.CPM_MAX_IMPRESSIONS]: i18n(
                'maximum-number-impressions'
            ),
            [GdCampaignStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD]: i18n(
                'maximum-number-impressions'
            )
        };
    }

    static getCampaignStrategyName(
        strategy: GdCampaignStrategyType,
        platform?: GdCampaignPlatform,
        isBidCorrectionSearchEnabled?: boolean
    ): string {
        const strategyName = StrategyHelpers.getCampaignStrategyNameEnumSetTitles(
            isBidCorrectionSearchEnabled
        )[strategy];

        switch (strategy) {
            case GdCampaignStrategyType.DEFAULT:
                if (platform) {
                    return StrategyHelpers.getCampaignDefaultStrategyPlatformSetTitles(
                        isBidCorrectionSearchEnabled
                    )[platform];
                }
                return strategyName;
            default:
                return strategyName;
        }
    }

    static getPlatformName(name?: GdCampaignPlatform): string {
        if (!name) {
            return '';
        }

        const mappings: Record<GdCampaignPlatform, string> = {
            [GdCampaignPlatform.BOTH]: i18n('on-both-platforms'),
            [GdCampaignPlatform.SEARCH]: i18n('on-search-platform'),
            [GdCampaignPlatform.CONTEXT]: i18n('on-net-platform')
        };

        return mappings[name];
    }

    static getTitle(
        name?: GdCampaignStrategyType,
        platform?: GdCampaignPlatform,
        isBidCorrectionSearchEnabled?: boolean
    ): string {
        if (!name) {
            return '';
        }

        return StrategyHelpers.getCampaignStrategyName(
            name,
            platform,
            isBidCorrectionSearchEnabled
        );
    }

    static formatCurrency(
        value: LogCampaignStrategyParamValue | null,
        currency: CurrencyCode
    ): string {
        if (value === null) {
            return '';
        }
        return `${value} ${mapStringToCurrencySymbol(currency)}`;
    }

    static isCpmCampaign(name?: GdCampaignStrategyType): boolean {
        if (!name) {
            return false;
        }

        return [
            GdCampaignStrategyType.CPM_DEFAULT,
            GdCampaignStrategyType.CPM_MAX_REACH,
            GdCampaignStrategyType.CPM_MAX_IMPRESSIONS,
            GdCampaignStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD,
            GdCampaignStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD
        ].includes(name);
    }

    static isOptimizationCampaign(name?: GdCampaignStrategyType): boolean {
        if (!name) {
            return false;
        }

        return [
            GdCampaignStrategyType.AVG_CPA_PER_CAMP,
            GdCampaignStrategyType.AVG_CPA_PER_FILTER,
            GdCampaignStrategyType.AVG_CPC_PER_CAMP,
            GdCampaignStrategyType.AVG_CPC_PER_FILTER
        ].includes(name);
    }
}

export class CampaignStrategyParam {
    private paramText: string;
    private valueText: string;
    private hasDash: boolean;

    constructor(
        protected readonly value: LogCampaignStrategyParamValue,
        protected readonly paramId:
            | Exclude<keyof LogCampaignStrategyParams, 'goal'>
            | OptimizationStrategyParamNames
            | Exclude<keyof LogCampaignDayBudget, 'period'>
            | GdCampaignStrategyType
            | '',
        protected readonly currency?: CurrencyCode
    ) {
        this.paramText = this.calcParamText();
        this.valueText = this.calcValueText();
        this.hasDash = this.calcHasDash();
    }

    public getValueText = (): string => {
        return this.valueText;
    };

    public getParamText = (): string => {
        return this.paramText;
    };

    public isValueSet = (): boolean => {
        return this.value !== null;
    };

    public getHasDash(): boolean {
        return this.hasDash;
    }

    protected calcParamText(): string {
        switch (this.paramId) {
            case StrategyParamNames.avgBid:
                return i18n('avgBid');
            case StrategyParamNames.bid:
                return i18n('bid');
            case StrategyParamNames.filterAvgBid:
                return i18n('filterAvgBid');
            case StrategyParamNames.avgCpa:
                return i18n('avgCpa');
            case StrategyParamNames.filterAvgCpa:
                return i18n('filterAvgCpa');
            case StrategyParamNames.sum:
                return i18n('sum');
            case StrategyParamNames.clicksLimit:
                return i18n('clicksLimit');
            case StrategyParamNames.profitability:
                return i18n('profitability');
            case StrategyParamNames.roiCoef:
                return i18n('roiCoef');
            case StrategyParamNames.reserveReturn:
                return i18n('reserveReturn');
            case StrategyParamNames.avgCpi:
                return i18n('avgCpi');
            case StrategyParamNames.avgCpm:
                return i18n('avgCpm');
            case StrategyParamNames.separateBidding:
                return i18n('separateBidding');
            case OptimizationStrategyParamNames.avgBidOpt:
                return i18n('avgBidOpt');
            case OptimizationStrategyParamNames.avgCpaOpt:
                return i18n('avgCpaOpt');
            case StrategyBudgetParamNames.autoProlongation:
                return i18n('autoProlongation');
            case StrategyBudgetParamNames.sum:
                return i18n('sum');
            case StrategyBudgetParamNames.showMode:
                return i18n('showMode');
            case StrategyBudgetParamNames.start:
                return i18n('start');
            case StrategyBudgetParamNames.finish:
                return i18n('finish');
            default:
                return '';
        }
    }

    protected calcValueText(): string {
        return StrategyHelpers.formatCurrency(
            this.value,
            this.currency || CurrencyCode.RUB
        );
    }

    protected calcHasDash(): boolean {
        return true;
    }
}

export class CampaignStrategyClicksLimitParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return this.value.toString();
    }
}

export class CampaignStrategySeparateBidParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return '';
    }

    protected calcHasDash(): boolean {
        return false;
    }
}

export class CampaignStrategyGoalParam extends CampaignStrategyParam {
    constructor(
        protected readonly value: LogCampaignStrategyParamValue,
        protected readonly paramId: GdCampaignStrategyType | ''
    ) {
        super(value, paramId);
    }

    protected calcValueText(): string {
        const value = this.value as CampaignStrategyGoal;

        if (this.paramId === GdCampaignStrategyType.WEEK_BUDGET) {
            switch (value.id) {
                case undefined:
                    return i18n('week-budget-max-clicks');
                case StrategyHelpers.MobileCPAGoalIds[0]:
                case StrategyHelpers.MobileCPAGoalIds[1]:
                    return i18n('week-budget-max-installations');
                case 0:
                    return i18n('vse-tseli');
                default:
                    return i18n('tsel', { goalName: value.name });
            }
        }
        return value.id === 0
            ? i18n('po-vsem-tselyam')
            : i18n('tsel', { goalName: value.name });
    }

    protected calcParamText(): string {
        const value = this.value as CampaignStrategyGoal;
        if (
            this.paramId === GdCampaignStrategyType.WEEK_BUDGET &&
            value.id !== undefined &&
            !StrategyHelpers.MobileCPAGoalIds.includes(value.id)
        ) {
            return i18n('maxCpa');
        } else {
            return '';
        }
    }

    protected calcHasDash(): boolean {
        const value = this.value as CampaignStrategyGoal;

        if (this.paramId === GdCampaignStrategyType.WEEK_BUDGET) {
            switch (value.id) {
                case undefined:
                    return false;
                case StrategyHelpers.MobileCPAGoalIds[0]:
                case StrategyHelpers.MobileCPAGoalIds[1]:
                    return false;
                case 0:
                    return true;
                default:
                    return true;
            }
        }

        return true;
    }
}

export class CampaignStrategyRoiCoefParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return (this.value as number).toFixed(2).toString();
    }
}

export class CampaignStrategyReserveReturnParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return i18n('reserveReturnValue', {
            reserveReturn: this.value as string
        });
    }

    protected calcHasDash(): boolean {
        return false;
    }
}

export class CampaignStrategyProfitabilityParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return i18n('profitabilityValue', {
            profitability: this.value as string
        });
    }

    protected calcHasDash(): boolean {
        return false;
    }
}

export class CampaignDayBudgetModeParam extends CampaignStrategyParam {
    constructor(
        protected readonly value: GdCampaignBudgetShowMode,
        protected readonly paramId: Exclude<
            keyof LogCampaignDayBudget,
            'period'
        >
    ) {
        super(value, paramId);
    }

    protected calcValueText(): string {
        switch (this.value) {
            case GdCampaignBudgetShowMode.DEFAULT:
                return `${i18n('default')} `;
            case GdCampaignBudgetShowMode.STRETCHED:
                return `${i18n('stretched')} `;
            default:
                return '';
        }
    }

    protected calcHasDash(): boolean {
        return false;
    }
}

export class CampaignDayBudgetSumParam extends CampaignStrategyParam {
    protected calcParamText(): string {
        return i18n('day-budget-sum');
    }
}

export class CampaignDayBudgetAutoProlongationParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        return i18n('autoProlongation');
    }

    protected calcParamText(): string {
        return '';
    }
}

export class CampaignDayBudgetDateParam extends CampaignStrategyParam {
    protected calcValueText(): string {
        const date = moment(this.value as string).clone();

        return DateDisplayHelper.getDateText(date, 'DD.MM.YYYY');
    }
}
